import { useState, useEffect, useCallback, useRef } from 'react';
import type { AuthContextType, AuthUser } from '../types/auth.types';
import { parseJwt } from 'utils/jwt';

export function useProvideAuth(): AuthContextType {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearLogoutTimer = useCallback(() => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('user_uid');
    localStorage.removeItem('org_uid');
    localStorage.removeItem('role');
    setUser(null);
    clearLogoutTimer();
  }, [clearLogoutTimer]);

  const login = useCallback(
    async (token: string, user_uid: string, org_uid: string, expiresIn: number) => {
      const expiry = Date.now() + expiresIn * 1000;
      const decoded = parseJwt(token);
      const role = decoded?.role || null;

      localStorage.setItem('token', token);
      localStorage.setItem('user_uid', user_uid);
      localStorage.setItem('org_uid', org_uid);
      localStorage.setItem('tokenExpiry', expiry.toString());
      localStorage.setItem('role', role);

      setUser({ token, user_uid, expiry, role });
      clearLogoutTimer();
      logoutTimerRef.current = setTimeout(logout, expiresIn * 1000);
    },
    [logout, clearLogoutTimer]
  );

  const hasRole = useCallback(
    (requiredRole: string | string[]) => {
      if (!user?.role) return false;

      if (Array.isArray(requiredRole)) {
        return requiredRole.includes(user.role);
      }
      return user.role === requiredRole;
    },
    [user]
  );

  // useEffect(() => {
  //   const token = localStorage.getItem('token');
  //   const uid = localStorage.getItem('user_uid');
  //   const expiryStr = localStorage.getItem('tokenExpiry');
  //   const expiry = expiryStr ? parseInt(expiryStr, 10) : 0;

  //   if (token && uid && expiry && Date.now() < expiry) {
  //     setUser({ token, user_uid: uid, expiry });
  //     const remaining = expiry - Date.now();
  //     clearLogoutTimer();
  //     logoutTimerRef.current = setTimeout(logout, remaining);
  //   } else {
  //     logout();
  //   }

  //   setIsLoading(false);
  //   return () => clearLogoutTimer();
  // }, [logout, clearLogoutTimer]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const uid = localStorage.getItem('user_uid');
    const orgUid = localStorage.getItem('org_uid');
    const expiryStr = localStorage.getItem('tokenExpiry');
    const storedRole = localStorage.getItem('role');
    const expiry = expiryStr ? parseInt(expiryStr, 10) : 0;

    if (token && uid && expiry && Date.now() < expiry) {
      // If role missing, decode again
      let role = storedRole;
      if (!role) {
        const decoded = parseJwt(token);
        role = decoded?.role || null;
      }

      setUser({ token, user_uid: uid, expiry, role });

      const remaining = expiry - Date.now();
      clearLogoutTimer();
      logoutTimerRef.current = setTimeout(logout, remaining);
    } else {
      logout();
    }

    setIsLoading(false);
    return () => clearLogoutTimer();
  }, [logout, clearLogoutTimer]);

  return { user, isLoading, login, logout, hasRole };
}
